import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import './Toast.css';

const ToastCtx = createContext({ notify: () => {} });

let idCounter = 0;
const nextId = () => ++idCounter;

function Toast({ toast, onDismiss }) {
  useEffect(() => {
    if (toast.duration === 0) return undefined;
    const t = setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);

  return (
    <div className={`toast toast--${toast.kind}`} role={toast.kind === 'error' ? 'alert' : 'status'}>
      <div className="toast__body">
        {toast.title && <div className="toast__title">{toast.title}</div>}
        <div className="toast__message">{toast.message}</div>
      </div>
      <button className="toast__close" onClick={() => onDismiss(toast.id)} aria-label="Fermer">×</button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback((opts) => {
    const t = {
      id: nextId(),
      kind: 'info',
      duration: 4000,
      ...(typeof opts === 'string' ? { message: opts } : opts),
    };
    setToasts((list) => [...list, t]);
    return t.id;
  }, []);

  // Convenience helpers attached to the function itself.
  notify.success = (msg, extra = {}) => notify({ kind: 'success', message: msg, ...extra });
  notify.error = (msg, extra = {}) => notify({ kind: 'error', message: msg, duration: 7000, ...extra });
  notify.info = (msg, extra = {}) => notify({ kind: 'info', message: msg, ...extra });

  // Catch any axios rejection that no page handled with try/catch and surface it.
  useEffect(() => {
    const onRejection = (event) => {
      const reason = event.reason;
      if (!reason) return;
      // Only show toast for axios-shaped errors so we don't fire for unrelated rejections.
      if (reason.isAxiosError || reason.userMessage) {
        notify.error(errorMessage(reason));
        event.preventDefault?.();
      }
    };
    window.addEventListener('unhandledrejection', onRejection);
    return () => window.removeEventListener('unhandledrejection', onRejection);
  }, [notify]);

  return (
    <ToastCtx.Provider value={{ notify, dismiss }}>
      {children}
      <div className="toast-stack" aria-live="polite">
        {toasts.map((t) => (
          <Toast key={t.id} toast={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useNotify() {
  return useContext(ToastCtx).notify;
}

/**
 * Translate any error (from axios or otherwise) into a user-friendly message,
 * using the `userMessage` set by the axios interceptor when available.
 */
export function errorMessage(err, fallback = 'Une erreur est survenue.') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;
  if (err.userMessage) return err.userMessage;
  if (err.response?.data?.message) return err.response.data.message;
  if (err.message && !err.message.startsWith('Request failed')) return err.message;
  return fallback;
}
